import { useEffect } from 'react';
import { RefreshCw, BusFront, MapPin, Clock3, ArrowRight } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SURFACES } from '@/lib/gql/__generated__/surfaces';
import { useSurfaceAccess } from '@/lib/platform/surfaces';
import { ROUTES } from './state/catalogue';
import { useScheduledBuses, useScheduledBusesActions } from './state';

function formatCheckedAt(value: string): string | null {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'America/Los_Angeles',
  }).format(date);
}

function ScheduleBoardContent() {
  const access = useSurfaceAccess(SURFACES['schedule-board']);
  const snapshot = useScheduledBuses((current) => current);
  const actions = useScheduledBusesActions();
  const context = snapshot.context;

  useEffect(() => {
    if (access === 'allowed') actions.loadSchedule();
  }, [access]);

  const selectedRoute = ROUTES.find((route) => route.id === context.route);
  const selectedDirection = selectedRoute?.directions.find((direction) => direction.id === context.direction);
  const checkedAt = formatCheckedAt(context.checkedAt);
  const isLoading = snapshot.matches('loading');
  const isError = snapshot.matches('error');
  const isReady = snapshot.matches('ready');
  const hasSelection = Boolean(selectedRoute && selectedDirection && context.stop);

  if (access === 'waiting') {
    return <StatusPanel title="Checking access" description="The schedule board is getting ready." />;
  }
  if (access === 'unavailable') {
    return (
      <StatusPanel
        title="Schedule board unavailable"
        description="We couldn’t check access to this screen. Please try again."
      />
    );
  }
  if (access === 'denied') {
    return (
      <StatusPanel title="This screen isn’t available" description="You don’t have access to the schedule board." />
    );
  }

  return (
    <main className="min-h-screen bg-background px-4 py-6 text-foreground sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-2xl">
        <header className="mb-7 flex items-start justify-between gap-4">
          <div>
            <div className="mb-3 flex items-center gap-2 text-primary">
              <BusFront aria-hidden="true" className="size-5" />
              <span className="text-sm font-semibold tracking-wide">Seattle Bus Times</span>
            </div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight sm:text-4xl">Bus times</h1>
            <p className="mt-2 max-w-prose text-sm leading-6 text-muted-foreground">
              Choose a route, direction, and stop to check the scheduled service for your ride.
            </p>
          </div>
          <Badge variant="outline" className="mt-1 shrink-0 bg-card px-2.5 py-1">
            Seattle
          </Badge>
        </header>

        <Card className="border-border/80 shadow-sm">
          <CardHeader className="border-b border-border/70">
            <CardTitle className="text-lg">Find a bus</CardTitle>
            <CardDescription>Start with the route you want to ride.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 pt-5">
            <div className="space-y-3">
              <Label>Route</Label>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {ROUTES.map((route) => {
                  const key =
                    route.id === 'rapidride-e-line'
                      ? 'choose-route-e'
                      : route.id === 'route-44'
                        ? 'choose-route-44'
                        : undefined;
                  return (
                    <Button
                      key={route.id}
                      type="button"
                      variant={context.route === route.id ? 'default' : 'outline'}
                      className="h-11 justify-between px-4"
                      data-jr-key={key}
                      onClick={() => actions.chooseRoute(route.id)}
                    >
                      <span>{route.label}</span>
                      {context.route === route.id && <ArrowRight aria-hidden="true" className="size-4" />}
                    </Button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-3">
              <Label>Direction</Label>
              <div className="grid grid-cols-2 gap-3">
                {(selectedRoute?.directions ?? []).map((direction) => {
                  const directionKey = direction.id.endsWith('bound')
                    ? `choose-direction-${direction.id.slice(0, -5)}`
                    : undefined;
                  return (
                    <Button
                      key={direction.id}
                      type="button"
                      variant={context.direction === direction.id ? 'secondary' : 'outline'}
                      className="h-11"
                      data-jr-key={directionKey}
                      onClick={() => actions.chooseDirection(direction.id)}
                      disabled={!selectedRoute}
                    >
                      {direction.label}
                    </Button>
                  );
                })}
                {!selectedRoute && <p className="col-span-2 text-sm text-muted-foreground">Choose a route first.</p>}
              </div>
            </div>

            <div className="space-y-3">
              <Label htmlFor="scheduled-stop">Stop</Label>
              <Select
                value={context.stop || null}
                onValueChange={(value) => {
                  if (typeof value === 'string') actions.chooseStop(value);
                }}
                items={(selectedDirection?.stops ?? []).map((stop) => ({ value: stop.id, label: stop.label }))}
              >
                <SelectTrigger id="scheduled-stop" data-jr-key="choose-stop" className="h-11 w-full bg-background">
                  <SelectValue placeholder={selectedDirection ? 'Select a stop' : 'Choose a direction first'} />
                </SelectTrigger>
                <SelectContent>
                  {(selectedDirection?.stops ?? []).map((stop) => (
                    <SelectItem key={stop.id} value={stop.id}>
                      {stop.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <section aria-live="polite" className="mt-5">
          {!hasSelection && (
            <Card className="border-dashed bg-card/70">
              <CardContent className="flex items-start gap-3 p-5">
                <MapPin aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-primary" />
                <div>
                  <h2 className="font-heading font-semibold">Choose a route to begin</h2>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">
                    Your usual stop will be remembered on this device once you choose one.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {hasSelection && (
            <Card className="overflow-visible">
              <CardHeader className="border-b border-border/70">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold tracking-widest text-muted-foreground uppercase">
                      Selected stop
                    </p>
                    <CardTitle className="mt-2 text-xl">
                      {selectedDirection?.stops.find((stop) => stop.id === context.stop)?.label}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      {selectedRoute?.label} · {selectedDirection?.label}
                    </CardDescription>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="min-h-11"
                    data-jr-key="change-selection"
                    onClick={() => actions.chooseRoute(context.route)}
                  >
                    Change selection
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-5 p-5">
                {isLoading && <p className="text-sm text-muted-foreground">Checking the latest schedule…</p>}
                {isError && (
                  <p className="text-sm text-destructive">
                    We couldn’t check the schedule right now. Please try again.
                  </p>
                )}
                {isReady && context.sourceStatus === 'missing' && (
                  <div className="rounded-md border border-accent bg-accent/40 p-4">
                    <p className="font-heading font-semibold">Schedule data isn’t available on this device</p>
                    <p className="mt-1 text-sm leading-6 text-muted-foreground">
                      There are no scheduled bus times to show yet. Refresh to check again.
                    </p>
                  </div>
                )}
                {isReady && context.sourceStatus === 'available' && context.buses.length > 0 && (
                  <div>
                    <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
                      <Clock3 aria-hidden="true" className="size-4 text-primary" />
                      Upcoming scheduled buses
                    </div>
                    <ul className="grid gap-2 sm:grid-cols-3">
                      {context.buses.slice(0, 3).map((bus) => (
                        <li
                          key={bus.time}
                          className="rounded-md bg-muted px-4 py-3 text-center font-heading text-lg font-semibold tabular-nums"
                        >
                          {bus.time}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {isReady && context.sourceStatus === 'available' && context.buses.length === 0 && (
                  <p className="rounded-md border border-border bg-muted/50 p-4 text-sm leading-6 text-muted-foreground">
                    No scheduled buses are available for this stop right now.
                  </p>
                )}
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/70 pt-4">
                  <div className="text-xs leading-5 text-muted-foreground">
                    <p>Seattle service day: {context.serviceDay || 'Not available'}</p>
                    <p>{checkedAt ? `Checked ${checkedAt}` : 'Checked time not available'}</p>
                  </div>
                  <Button
                    type="button"
                    className="min-h-11 gap-2"
                    data-jr-key="refresh-scheduled-buses"
                    onClick={() => actions.refresh()}
                    disabled={isLoading}
                  >
                    <RefreshCw aria-hidden="true" className={isLoading ? 'size-4 animate-spin' : 'size-4'} />
                    Refresh
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </section>
      </div>
    </main>
  );
}

function StatusPanel({ title, description }: { title: string; description: string }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4 py-8">
      <Card className="w-full max-w-md">
        <CardContent className="p-6">
          <h1 className="font-heading text-xl font-semibold">{title}</h1>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">{description}</p>
        </CardContent>
      </Card>
    </main>
  );
}

export function ScheduleBoardPage() {
  return <ScheduleBoardContent />;
}
